import type { SupabaseClient } from '@supabase/supabase-js';
import type { Ability, UserAbility } from '../types/index.js';
import type { ActiveAbility, ActiveSkill, ActiveAgent, ActiveMcp, ActiveSkillFile, StorageAssetRef } from './types.js';
import { parseAbilityHooks } from './hooks.js';
import { parseMcpConnectionConfig } from '../items/index.js';
import { loadCustomerAbilities } from './customer-shelf.js';
import { dropSystemShadowed } from './system-shadow.js';
import { createLogger } from '../utils/logger.js';

const logger = createLogger('AbilitiesLoad');

/**
 * ABILITIES_SLIM_V1 (agents-first lane A): an ability is a set of skills and MCPs.
 * When `slimAgents` is set the ability_agents join is not read at all and every
 * ability loads with `agents: []`. Threaded from each server call site the way
 * FormatOpts.formatV2 is (never read from process.env here: this module is
 * home-agnostic). Absent or false = today's load, byte-identical.
 *
 * `companyShelf` (Platform API, PLATFORM_API_V1): also load the user's company
 * shelf (customer-shelf.ts). The box shelf, its head and the MCP runtime pass the
 * same flag so they agree. Absent or false = today's reads, byte-identical.
 */
export interface AbilityLoadOpts {
  slimAgents?: boolean;
  companyShelf?: boolean;
}

/**
 * Returns all abilities that should be active for the given user, with the
 * canonical items they bundle.
 *
 * Includes:
 *   - all system-scope abilities
 *   - marketplace abilities the user has explicitly installed (via user_abilities)
 *   - user-OWNED abilities (scope='user', owner_id=userId)
 *
 * After the 20260529100000 consolidation migration, every bundled skill/agent
 * surfaces through the ability_skills / ability_agents join tables. A skill's
 * reference/example files (the `references/*.md`, `examples/*.html` that SKILL.md
 * instructs the agent to read) live inline in the skill row's `files` jsonb
 * (`InlineBundleFile[]`); the materializer writes them next to SKILL.md so the
 * agent can actually run the skill. Binary / large assets live in
 * `storage_assets` jsonb (downloaded by the materializer when it needs them).
 */

/** Map a skill row's `files` jsonb (InlineBundleFile[]) → ActiveSkillFile[]. */
function mapSkillFiles(raw: unknown): ActiveSkillFile[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((f): f is { path: string; content: string } =>
      !!f && typeof (f as { path?: unknown }).path === 'string' &&
      typeof (f as { content?: unknown }).content === 'string')
    .map((f) => ({ path: f.path, content: f.content, is_binary: false }));
}

/** Map a skill row's `storage_assets` jsonb → StorageAssetRef[] (validated shape). */
function mapStorageAssets(raw: unknown): StorageAssetRef[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((a): a is StorageAssetRef =>
      !!a && typeof (a as { key?: unknown }).key === 'string' &&
      typeof (a as { target_path?: unknown }).target_path === 'string' &&
      typeof (a as { size?: unknown }).size === 'number')
    .map((a) => ({
      key: a.key,
      target_path: a.target_path,
      size: a.size,
      ...(a.mime_type ? { mime_type: a.mime_type } : {}),
      ...(a.kind ? { kind: a.kind } : {}),
    }));
}
export async function loadActiveAbilities(
  supabase: SupabaseClient,
  userId: string,
  opts?: AbilityLoadOpts,
): Promise<ActiveAbility[]> {
  // 1) System abilities (always loaded)
  const { data: systemRows, error: sysErr } = await supabase
    .from('abilities')
    .select('*')
    .eq('scope', 'system');
  if (sysErr) throw sysErr;

  // 2) user_abilities for this user (marketplace installs)
  const { data: userAbilities, error: uaErr } = await supabase
    .from('user_abilities')
    .select('*')
    .eq('user_id', userId);
  if (uaErr) throw uaErr;

  let installedRows: Ability[] = [];
  if ((userAbilities as UserAbility[] | null)?.length) {
    const ids = (userAbilities as UserAbility[]).map((ua) => ua.ability_id);
    const { data: installed, error: insErr } = await supabase
      .from('abilities')
      .select('*')
      .in('id', ids);
    if (insErr) throw insErr;
    // A company ability is never an install: user_abilities' row policy lets any
    // user attach any id, so a customer row arriving this way is dropped.
    installedRows = ((installed as Ability[]) ?? []).filter((a) => a.scope !== 'customer');
  }

  // 3) User-OWNED abilities (scope='user', owner_id=userId). Per spec §4b,
  // ownership via abilities.owner_id is enough — no user_abilities row.
  const { data: ownedRows, error: ownErr } = await supabase
    .from('abilities')
    .select('*')
    .eq('scope', 'user')
    .eq('owner_id', userId);
  if (ownErr) throw ownErr;

  // 3b) Customer-scope abilities: the user's company shelf (Platform API), keyed
  // by users.org_id, with notebook walls applied.
  const customerRows = opts?.companyShelf ? await loadCustomerAbilities(supabase, userId) : [];

  // Dedup by id — system + marketplace-installed + user-owned. Map de-dupes
  // so an ability appearing in two sets surfaces only once.
  const allAbilities = new Map<string, Ability>();
  for (const a of (systemRows as Ability[] | null) ?? []) allAbilities.set(a.id, a);
  for (const a of installedRows) allAbilities.set(a.id, a);
  for (const a of (ownedRows as Ability[] | null) ?? []) allAbilities.set(a.id, a);
  for (const a of customerRows) allAbilities.set(a.id, a);
  // A non-system ability under a system slug stays out: it would take over that ability's folder (system-shadow.ts).
  const { shadowed } = dropSystemShadowed([...allAbilities.values()]);
  if (shadowed.length > 0) {
    for (const a of shadowed) allAbilities.delete(a.id);
    logger.warn('[abilities] left non-system abilities under a system slug out of the active set', {
      userId,
      shadowed: shadowed.map((a) => ({ id: a.id, slug: a.slug, scope: a.scope })),
    });
  }

  if (allAbilities.size === 0) return [];

  const abilityIds = Array.from(allAbilities.keys());

  // 4) Joined skills via ability_skills. The canonical skills table now holds
  // the metadata in a jsonb column; pull what we need to synthesize SKILL.md.
  const { data: skillJoinRows, error: skErr } = await supabase
    .from('ability_skills')
    .select('ability_id, skill_id, skill:skills(id, slug, content, metadata, files, storage_assets)')
    .in('ability_id', abilityIds);
  if (skErr) throw skErr;

  const skillsByAbility = new Map<string, ActiveSkill[]>();
  for (const row of (skillJoinRows as any[] | null) ?? []) {
    if (!row.skill) continue;
    const list = skillsByAbility.get(row.ability_id) ?? [];
    list.push(mintActiveSkill(row.skill, allAbilities.get(row.ability_id)?.scope !== 'marketplace'));
    skillsByAbility.set(row.ability_id, list);
  }

  // 5) Joined agents via ability_agents. Agent content lives in `content`,
  // metadata fields (avatar_url, color, description) in `metadata`.
  // ABILITIES_SLIM_V1: the join is not read; every ability serves agents: [].
  const agentsByAbility = new Map<string, ActiveAgent[]>();
  if (!opts?.slimAgents) {
    const { data: agentJoinRows, error: agErr } = await supabase
      .from('ability_agents')
      .select('ability_id, agent_id, agent:agents(id, slug, content, metadata)')
      .in('ability_id', abilityIds);
    if (agErr) throw agErr;
    for (const row of (agentJoinRows as any[] | null) ?? []) {
      if (!row.agent) continue;
      const meta = (row.agent.metadata ?? {}) as Record<string, unknown>;
      const list = agentsByAbility.get(row.ability_id) ?? [];
      list.push({
        id: row.agent.id,
        slug: row.agent.slug,
        description: (meta.description as string | undefined) ?? '',
        avatar_url: (meta.avatar_url as string | undefined) ?? null,
        color: (meta.color as string | undefined) ?? null,
        prompt: row.agent.content ?? null,
        ...agentMetaFields(meta),
      });
      agentsByAbility.set(row.ability_id, list);
    }
  }

  // 6) Joined MCPs via ability_mcps. Unlike skills/agents, an MCP row carries a
  // connection descriptor (connection_config) parsed into the typed union.
  const { data: mcpJoinRows, error: mcErr } = await supabase
    .from('ability_mcps')
    .select('ability_id, mcp_id, mcp:mcps(id, slug, content, metadata, connection_config)')
    .in('ability_id', abilityIds);
  if (mcErr) throw mcErr;

  const mcpsByAbility = new Map<string, ActiveMcp[]>();
  for (const row of (mcpJoinRows as any[] | null) ?? []) {
    if (!row.mcp) continue;
    const list = mcpsByAbility.get(row.ability_id) ?? [];
    list.push({
      id: row.mcp.id,
      slug: row.mcp.slug,
      content: row.mcp.content ?? '',
      metadata: (row.mcp.metadata ?? {}) as Record<string, unknown>,
      connection_config: parseMcpConnectionConfig(row.mcp.connection_config),
    });
    mcpsByAbility.set(row.ability_id, list);
  }

  // The canonical model has no per-ability file bag; everything lives in
  // joined items. `files` is preserved on ActiveAbility for the materializer
  // contract but is always empty under the consolidated schema.
  return Array.from(allAbilities.values()).map((ability) => ({
    ability,
    files: [],
    skills: skillsByAbility.get(ability.id) ?? [],
    agents: agentsByAbility.get(ability.id) ?? [],
    mcps: mcpsByAbility.get(ability.id) ?? [],
  }));
}

/** Lenient narrowing for metadata jsonb — a malformed value drops the field
 *  rather than failing the load (hint-shaped catalog data, never a gate). */
const metaStr = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);
const metaStrArray = (v: unknown): string[] | null =>
  Array.isArray(v) && v.length > 0 && v.every((x) => typeof x === 'string') ? (v as string[]) : null;

/**
 * THE mint of a served skill entry — the one place a `skills` row becomes the
 * {@link ActiveSkill} both the full shelf (loadActiveAbilities) and the eager install
 * (loadOneAbility) serve, and a /v1 version's pinned revision too (apps/server
 * platform/abilities/pinned.ts). One mint, because under HOOK_FINGERPRINT_V1 the checksum
 * hashes this entry whole (materialize.ts computeChecksum): two mints that drifted
 * would hash the same skill two ways and re-materialize it on every pass.
 *
 * Every key minted here is served to the box and hashed. Never mint a volatile value
 * (a timestamp, a counter, a signed URL) onto it: it would move every user's version
 * on every change. (The ability ROW does carry volatile columns — installed_count,
 * updated_at — which is why the checksum takes only its id + version.)
 */
export function mintActiveSkill(
  skill: {
    id: string;
    slug: string;
    content?: string | null;
    metadata?: unknown;
    files?: unknown;
    storage_assets?: unknown;
  },
  /**
   * False for a skill served under a marketplace ability: its rows are the publisher's (publish links
   * them, and a later edit by the publisher flows to every installer), so its hooks are never served,
   * registered or written into the SKILL.md on disk, where the Claude Code engine would run them.
   */
  servesHooks: boolean,
): ActiveSkill {
  const meta = (skill.metadata ?? {}) as Record<string, unknown>;
  const hooks = servesHooks ? parseAbilityHooks(meta.hooks) : undefined;
  const assets = mapStorageAssets(skill.storage_assets);
  return {
    id: skill.id,
    slug: skill.slug,
    description: (meta.description as string | undefined) ?? '',
    icon: (meta.icon as string | undefined) ?? null,
    content: skill.content ?? null,
    ...skillMetaFields(meta),
    // K6-P2 B6: declared shell-command hooks (Claude Code shape) live in the
    // skill's metadata jsonb. Parsed (lenient + total) into the typed config;
    // absent/malformed → field omitted → no hooks register for this skill.
    ...(hooks ? { hooks } : {}),
    // Reference/example files so the materializer writes them next to
    // SKILL.md — without these the agent reads instructions to open files
    // that don't exist and falls back to plain markdown.
    files: mapSkillFiles(skill.files),
    ...(assets.length ? { storage_assets: assets } : {}),
  };
}

/** Optional catalog/gating skill metadata carried verbatim into the synthesis
 *  struct (each key emitted only-when-present downstream, so legacy rows stay
 *  byte-identical). `allowed-tools` maps to `allowed_tools` (Claude gating key). */
function skillMetaFields(meta: Record<string, unknown>) {
  const category = metaStr(meta.category);
  const color = metaStr(meta.color);
  const allowedTools = metaStr(meta['allowed-tools']);
  const tools = metaStrArray(meta.tools);
  const mcps = metaStrArray(meta.mcps);
  return {
    ...(category ? { category } : {}),
    ...(color ? { color } : {}),
    ...(allowedTools ? { allowed_tools: allowedTools } : {}),
    ...(tools ? { tools } : {}),
    ...(mcps ? { mcps } : {}),
    ...(meta.args_schema !== undefined ? { args_schema: meta.args_schema } : {}),
  };
}

function agentMetaFields(meta: Record<string, unknown>) {
  const icon = metaStr(meta.icon);
  const model = metaStr(meta.model);
  const tools = metaStrArray(meta.tools);
  return { ...(icon ? { icon } : {}), ...(model ? { model } : {}), ...(tools ? { tools } : {}) };
}

/**
 * Returns a single ability with its joined skills + agents. Returns null if
 * the ability row doesn't exist.
 *
 * Use this for the eager-install path where the server needs only the
 * newly-installed ability, not the user's full active set. Each join query
 * is scoped to one ability_id.
 */
export async function loadOneAbility(
  supabase: SupabaseClient,
  abilityId: string,
  opts?: AbilityLoadOpts,
): Promise<ActiveAbility | null> {
  // 1) The ability row.
  const { data: abilityRows, error: aErr } = await supabase
    .from('abilities')
    .select('*')
    .eq('id', abilityId)
    .limit(1);
  if (aErr) throw aErr;
  const ability = (abilityRows as Ability[] | null)?.[0];
  if (!ability) return null;

  // 2) Joined skills via ability_skills (canonical-row metadata in jsonb).
  const { data: skillJoinRows, error: skErr } = await supabase
    .from('ability_skills')
    .select('skill_id, skill:skills(id, slug, content, metadata, files, storage_assets)')
    .eq('ability_id', abilityId);
  if (skErr) throw skErr;

  const skills: ActiveSkill[] = [];
  for (const row of (skillJoinRows as any[] | null) ?? []) {
    if (!row.skill) continue;
    skills.push(mintActiveSkill(row.skill, ability.scope !== 'marketplace'));
  }

  // 3) Joined agents via ability_agents (skipped under ABILITIES_SLIM_V1).
  const agents: ActiveAgent[] = [];
  if (!opts?.slimAgents) {
    const { data: agentJoinRows, error: agErr } = await supabase
      .from('ability_agents')
      .select('agent_id, agent:agents(id, slug, content, metadata)')
      .eq('ability_id', abilityId);
    if (agErr) throw agErr;
    for (const row of (agentJoinRows as any[] | null) ?? []) {
      if (!row.agent) continue;
      const meta = (row.agent.metadata ?? {}) as Record<string, unknown>;
      agents.push({
        id: row.agent.id,
        slug: row.agent.slug,
        description: (meta.description as string | undefined) ?? '',
        avatar_url: (meta.avatar_url as string | undefined) ?? null,
        color: (meta.color as string | undefined) ?? null,
        prompt: row.agent.content ?? null,
        ...agentMetaFields(meta),
      });
    }
  }

  // 4) Joined MCPs via ability_mcps (scoped to this ability). Each row carries a
  // connection descriptor parsed into the typed union.
  const { data: mcpJoinRows, error: mcErr } = await supabase
    .from('ability_mcps')
    .select('mcp_id, mcp:mcps(id, slug, content, metadata, connection_config)')
    .eq('ability_id', abilityId);
  if (mcErr) throw mcErr;

  const mcps: ActiveMcp[] = [];
  for (const row of (mcpJoinRows as any[] | null) ?? []) {
    if (!row.mcp) continue;
    mcps.push({
      id: row.mcp.id,
      slug: row.mcp.slug,
      content: row.mcp.content ?? '',
      metadata: (row.mcp.metadata ?? {}) as Record<string, unknown>,
      connection_config: parseMcpConnectionConfig(row.mcp.connection_config),
    });
  }

  return {
    ability,
    // No per-ability file bag in the canonical model. Synthesized SKILL.md /
    // AGENT.md / plugin.json are produced at materialize-time, not stored.
    files: [],
    skills,
    agents,
    mcps,
  };
}

